import cx from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useScreenSize from 'hooks/size';
import { LinkButton } from 'app/shared/button';
import { useUserService } from 'hooks/iam/user';
import { addDataToStore } from 'store/actions/user-data';
import { TOUR_STEPS } from './steps';
import styles from './tour.module.css';

const SPOTLIGHT_PADDING = 6;
const CARD_WIDTH = 400;
const CARD_GAP = 16;

const findTarget = (target) => (target ? document.querySelector(`[data-tour="${target}"]`) : null);

const getCardStyle = (rect) => {
  if (!rect) return undefined;

  const viewport_width = window.innerWidth;
  const viewport_height = window.innerHeight;

  let left = rect.right + CARD_GAP;
  if (left + CARD_WIDTH > viewport_width - CARD_GAP) {
    left = Math.max(CARD_GAP, rect.left - CARD_WIDTH - CARD_GAP);
  }

  let top = Math.min(rect.top, viewport_height - 260);
  top = Math.max(CARD_GAP, top);

  return { top, left };
};

export const OnboardingTour = () => {
  const dispatch = useDispatch();
  const user_data = useSelector((state) => state.user_data);
  const { isDesktop, isMiniDesktop } = useScreenSize();
  const { updateUserData } = useUserService();

  const { id: user_id, onboarding } = user_data;
  const has_completed_tour = !!onboarding?.has_completed_tour;

  const [steps, setSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [active, setActive] = useState(false);

  const current = steps[index];

  // Start the tour once per user, on a layout where the targets are visible.
  // Completion is stored on the user's metadata, so a user who has already
  // taken the tour won't see it again — even in a different organisation.
  useEffect(() => {
    if (!user_id || has_completed_tour) return;
    if (!isDesktop && !isMiniDesktop) return;

    const timer = setTimeout(() => {
      const resolved = TOUR_STEPS.filter((step) => !step.target || findTarget(step.target));
      if (!resolved.length) return;
      setSteps(resolved);
      setIndex(0);
      setActive(true);
    }, 800);

    return () => clearTimeout(timer);
  }, [user_id, has_completed_tour, isDesktop, isMiniDesktop]);

  const updateRect = useCallback(() => {
    if (!current || !current.target) {
      setRect(null);
      return;
    }
    const element = findTarget(current.target);
    setRect(element ? element.getBoundingClientRect() : null);
  }, [current]);

  useLayoutEffect(() => {
    if (!active) return undefined;

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [active, updateRect]);

  const dismiss = (was_skipped) => {
    setActive(false);
    if (!user_id || has_completed_tour) return;

    const next_onboarding = {
      ...(onboarding || {}),
      has_completed_tour: true,
      tour_skipped: was_skipped
    };
    dispatch(addDataToStore({ ...user_data, onboarding: next_onboarding }));
    updateUserData(user_id, { data: { onboarding: next_onboarding } });
  };

  const goNext = () => {
    if (index >= steps.length - 1) {
      dismiss(false);
      return;
    }
    setIndex((prev) => prev + 1);
  };

  const goBack = () => setIndex((prev) => Math.max(0, prev - 1));

  if (!active || !current) return null;

  const is_last_step = index === steps.length - 1;
  const spotlight_style = rect
    ? {
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2
      }
    : { top: '50%', left: '50%', width: 0, height: 0 };

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Onboarding tour">
      <div className={styles.spotlight} style={spotlight_style} />

      <div
        className={cx(styles.card, { [styles.cardCentered]: !rect })}
        style={rect ? getCardStyle(rect) : undefined}
      >
        <span className={styles.counter}>
          Step {index + 1} of {steps.length}
        </span>
        <h4 className={styles.title}>{current.title}</h4>
        <p className={styles.body}>{current.body}</p>

        <div className={styles.dots}>
          {steps.map((step, i) => (
            <span
              key={step.title}
              className={cx(styles.dot, { [styles.dotActive]: i === index })}
            />
          ))}
        </div>

        <div className={styles.actions}>
          <LinkButton className={styles.skip} text="Skip tour" onClick={() => dismiss(true)} />
          <div className={styles.navBtns}>
            {index > 0 && (
              <button type="button" className={styles.btnGhost} onClick={goBack}>
                Back
              </button>
            )}
            <button type="button" className={styles.btnPrimary} onClick={goNext}>
              {is_last_step ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
